// DEPENDENCIES ---------------------------------------------------------------- //

import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { useUser } from '@store';

import { css } from '@emotion/css';
import { Box, Theme, useTheme, IconButton, Tooltip, Typography } from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';

import HeaderAvatar from './HeaderAvatar';
import { HeaderSearch } from '@features/search';

/** Map top-level route segments to page titles */
const ROUTE_TITLES: Record<string, string> = {
  dashboard: 'Dashboard',
  timeline: 'Timeline',
  unassigned: 'Unassigned',
  chat: 'Chat',
  planning: 'Planning',
  confirmations: 'Confirmations',
  orders: 'All orders',
  moves: 'Moves',
  drivers: 'Drivers',
  pipeline: 'Pipeline',
  'driver-schedules': 'Driver Schedules',
  customers: 'Customers',
  outcomes: 'Outcomes',
  hangtags: 'Hangtags',
  plates: 'License Plates',
  insurance: 'Insurance Rates',
  lanes: 'Lanes',
  locations: 'Locations',
  regions: 'Regions',
  'region-pipelines': 'Region Pipelines',
  settings: 'Settings',
  certifications: 'Certifications',
  eventlogs: 'Event Logs',
  workflows: 'Workflows',
  'change-tracking': 'Change Tracking',
  profile: 'Profile',
  search: 'Search',
};

function usePageTitle(): string {
  const location = useLocation();
  const segment = location.pathname.split('/').filter(Boolean)[0] || '';
  return ROUTE_TITLES[segment] || 'Admin Portal';
}

interface HeaderProps {
  condensed: boolean;
  handleNavToggle: () => void;
  pageActions?: React.ReactNode;
}

const Header = ({ condensed, handleNavToggle, pageActions }: HeaderProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const user = useUser();
  const pageTitle = usePageTitle();

  return (
    <header className={cls.header}>
      <div className={cls.flex}>
        <Tooltip title={condensed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <IconButton edge='start' onClick={handleNavToggle} sx={{ mr: 1, color: 'text.primary' }}>
            {condensed ? <MenuIcon /> : <MenuOpenIcon />}
          </IconButton>
        </Tooltip>

        <Typography
          variant={'PageHeader' as any}
          sx={{
            flex: 1,
            color: 'text.primary',
            lineHeight: '64px',
          }}
        >
          {pageTitle}
        </Typography>

        {user && <HeaderSearch />}

        {pageActions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>{pageActions}</Box>}

        {user && <HeaderAvatar />}
      </div>
    </header>
  );
};

const useStyles = (theme?: Theme) => ({
  header: css`
    z-index: ${theme?.zIndex?.appBar};
    position: sticky;
    display: flex;
    align-items: center;
    top: 0;
    left: 0;
    width: 100%;
    min-height: 64px;
    max-height: 64px;
    padding: 0 ${theme?.spacing(4)};
    background-color: ${theme?.palette?.background?.paper};
    color: ${theme?.palette?.text?.primary};
    border-bottom: 1px solid ${theme?.palette?.divider};
  `,
  flex: css`
    display: flex;
    align-items: center;
    width: 100%;
  `,
});

export default Header;
